"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { subscribeUserToBlog } from "@/lib/api/blogs"
import { CryptoUser } from "@/types/users"
import { Blog } from "@types"
import { Loader2 } from "lucide-react"
import Link from "next/link"
// import SubscribeModalButton from "@/app/@modal/(.)subscribe/components/SubscribeButton"

export default function SubscribeButtonInlineClickHandler({
  blog,
  referrer,
  onSubscribeCallback,
  isUserLoggedIn,
  children,
  delay,
}: {
  blog: Pick<Blog, "id" | "lowercase_url">
  referrer?: Blog
  onSubscribeCallback?: (blogId: string) => void
  isUserLoggedIn: boolean
  children: React.ReactNode
  delay?: number
}) {
  const [loading, setLoading] = useState(false)
  if (!blog.id) return null

  const handleSubscribe = async () => {
    setLoading(true)

    const referrerInfo = referrer
      ? {
          id: referrer.id,
          wallet:
            referrer.collectibleWalletAddress ||
            (referrer.user as CryptoUser)?.lowercase_wallet_address,
        }
      : undefined

    await subscribeUserToBlog({
      lowercaseBlogUrl: blog.lowercase_url,
      blogId: blog.id,
      skipWelcomeEmail: true,
      referrer: referrerInfo,
    })

    onSubscribeCallback?.(blog.id)

    // Delay to allow the row to fade out if appropriate.
    if (delay) {
      setTimeout(() => {
        setLoading(false)
      }, delay)
    } else {
      setLoading(false)
    }
  }

  if (!isUserLoggedIn) {
    return (
      <Link
        href={`/${blog.lowercase_url}/subscribe`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <SubscribeButton loading={loading}>{children}</SubscribeButton>
      </Link>
    )
  }

  return (
    <>
      <SubscribeButton onClick={handleSubscribe} loading={loading}>
        {children}
      </SubscribeButton>
      {/* Use this button instead once the new subscribe modal is ready. */}
      {/* <SubscribeModalButton blogSlug={blog.lowercase_url} loading={loading}>
        {children}
      </SubscribeModalButton> */}
    </>
  )
}

function SubscribeButton({
  children,
  onClick,
  loading,
}: {
  children: React.ReactNode
  onClick?: () => void
  loading: boolean
}) {
  return (
    <Button
      className="px-2 py-2"
      variant={"outline"}
      onClick={onClick}
      disabled={loading}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin mx-0.5 my-1" />
      ) : (
        children
      )}
    </Button>
  )
}
